import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { VehicleForm } from './vehicle-form';
import {
	buildCreateVehicleInput,
	generateIdempotentVehicleId,
	type VehicleFormSubmitData
} from './vehicle-input';
import {
	VEHICLE_CREATE_MUTATION,
	VEHICLE_TYPES_QUERY
} from './vehicles.gql.tsx';

interface CloneData {
	vehicleId?: string; // Pre-generated ID from clone function
	plateNumber?: string | null;
	make?: string | null;
	model?: string | null;
	color?: string | null;
	vehicleTypeId?: number | null;
	customerId?: string | null;
	customerDisplayName?: string; // Display name for customer autocomplete
	isActive?: boolean;
}

interface LocationState {
	cloneData?: CloneData;
}

type VehicleTypeOption = {
	vehicleTypeId: number;
	name?: string | null;
};

export const VehiclesCreate = () => {
	const { t } = useTranslation();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;
	const { setPageHeader, resetPageHeader } = usePageHeader();

	// Use the pre-generated vehicleId from clone data, or generate a new one
	const idempotentVehicleId = useMemo(
		() => cloneData?.vehicleId ?? generateIdempotentVehicleId(),
		[cloneData?.vehicleId]
	);

	const {
		data: vehicleTypesData,
		loading: isVehicleTypeLoading,
		error: vehicleTypesError
	} = useQuery<{ vehicleTypes: VehicleTypeOption[] }>(VEHICLE_TYPES_QUERY);

	const vehicleTypeOptions = useMemo(
		() =>
			(vehicleTypesData?.vehicleTypes ?? [])
				.filter(type => type?.vehicleTypeId != null)
				.map(type => ({
					value: String(type.vehicleTypeId),
					label: type.name ?? `Type ${type.vehicleTypeId}`
				})),
		[vehicleTypesData]
	);

	const [createVehicle, { loading: isSubmitting }] = useMutation(
		VEHICLE_CREATE_MUTATION
	);

	useEffect(() => {
		setPageHeader({
			title: t('vehicles:create.title'),
			description: t('vehicles:create.description'),
			showBackButton: true,
			onBackClick: () => go({ to: '/vehicles' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, t]);

	const handleSubmit = async (formData: VehicleFormSubmitData) => {
		const payload = buildCreateVehicleInput(formData, idempotentVehicleId);

		try {
			const { data } = await createVehicle(
				getMutationOptions('vehicles', { input: payload })
			);

			const message = extractCheckResultError(data?.createVehicle);

			if (message) {
				showMutationErrorToast('vehicles', 'create', { message });
				return;
			}

			showMutationSuccessToast('vehicles', 'create');
			go({
				to: '/vehicles',
				state: { selectVehicleId: idempotentVehicleId }
			});
		} catch (error) {
			showMutationErrorToast('vehicles', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			{vehicleTypesError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load vehicle types</AlertTitle>
					<AlertDescription>
						{vehicleTypesError.message}
					</AlertDescription>
				</Alert>
			)}

			<VehicleForm
				mode={cloneData ? 'clone' : 'create'}
				initialData={cloneData ? {
					plateNumber: cloneData.plateNumber ?? '',
					vehicleTypeId: cloneData.vehicleTypeId ? String(cloneData.vehicleTypeId) : '',
					customerId: cloneData.customerId ?? null,
					customerDisplayName: cloneData.customerDisplayName,
					make: cloneData.make ?? '',
					model: cloneData.model ?? '',
					color: cloneData.color ?? '',
					isActive: cloneData.isActive ?? true
				} : undefined}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				vehicleTypeOptions={vehicleTypeOptions}
				isVehicleTypeLoading={isVehicleTypeLoading}
				headerTitle={t('vehicles:create.title')}
			/>
		</FormPageLayout>
	);
};
